import { create } from 'zustand';
import {
  PublicTopic,
  PostCard,
  PublicPost,
  PostSearchResult,
  BlogSearchFilters,
  BlogSearchQuery,
  SortOption,
  PaginationInfo
} from '@/types/blog';
import { BlogApi } from '@/services/blogApi';

interface BlogSearchState {
  searchQuery: string;
  selectedTopicId?: number;
  sortBy: SortOption;
  page: number;
  limit: number;
}

interface BlogStore {
  // Data state
  posts: PostCard[];
  featuredPosts: PostCard[];
  searchResults: PostSearchResult[];
  currentPost: PublicPost | null;
  topics: PublicTopic[];
  currentTopic: PublicTopic | null;
  
  // Search and filtering
  searchState: BlogSearchState;
  
  // UI state
  loading: boolean;
  searchLoading: boolean;
  topicsLoading: boolean;
  error: string | null;
  
  // Pagination
  pagination: PaginationInfo | null;
  
  // Actions - Data fetching
  fetchPosts: (filters?: BlogSearchFilters) => Promise<void>;
  fetchFeaturedPosts: () => Promise<void>;
  fetchTopics: () => Promise<void>;
  fetchPost: (uuid: string) => Promise<void>;
  fetchTopicBySlug: (topicSlug: string) => Promise<void>;
  fetchPostsByTopic: (topicSlug: string, filters?: Omit<BlogSearchFilters, 'topic_id'>) => Promise<void>;
  
  // Actions - Search
  searchPosts: (query: string, filters?: Omit<BlogSearchQuery, 'q'>) => Promise<void>;
  clearSearchResults: () => void;
  
  // Actions - Pagination
  loadPage: (page: number) => Promise<void>;
  loadNextPage: () => Promise<void>;
  loadPrevPage: () => Promise<void>;
  
  // Actions - Filters
  updateSearchState: (state: Partial<BlogSearchState>) => void;
  setSelectedTopic: (topic: PublicTopic | null) => void;
  setSortOrder: (sort: SortOption) => Promise<void>;
  
  // Actions - Interactions
  likePost: (uuid: string) => Promise<void>;
  sharePost: (uuid: string, platform: 'facebook' | 'linkedin' | 'zalo') => Promise<void>;
  
  // Actions - Utils
  clearError: () => void;
  resetState: () => void;
}

const defaultSearchState: BlogSearchState = {
  searchQuery: '',
  selectedTopicId: undefined,
  sortBy: 'newest',
  page: 1,
  limit: 10,
};

export const useBlogStore = create<BlogStore>((set, get) => ({
  // Initial state
  posts: [],
  featuredPosts: [],
  searchResults: [],
  currentPost: null,
  topics: [],
  currentTopic: null,
  searchState: defaultSearchState,
  loading: false,
  searchLoading: false,
  topicsLoading: false,
  error: null,
  pagination: null,
  
  // Data fetching actions
  fetchPosts: async (filters?: BlogSearchFilters) => {
    set({ loading: true, error: null });
    
    try {
      const result = await BlogApi.getPosts(filters);
      set({ 
        posts: result.posts, 
        pagination: result.pagination,
        loading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch posts',
        loading: false,
        posts: [],
        pagination: null
      });
    }
  },
  
  fetchFeaturedPosts: async () => {
    set({ loading: true, error: null });
    
    try {
      const featuredPosts = await BlogApi.getFeaturedPosts();
      set({ 
        featuredPosts, 
        loading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch featured posts',
        loading: false,
        featuredPosts: []
      });
    }
  },
  
  fetchTopics: async () => {
    set({ topicsLoading: true, error: null });
    
    try {
      const topics = await BlogApi.getTopics();
      set({ 
        topics, 
        topicsLoading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch topics',
        topicsLoading: false,
        topics: []
      });
    }
  },
  
  fetchPost: async (uuid: string) => {
    set({ loading: true, error: null });
    
    try {
      const post = await BlogApi.getPost(uuid);
      set({ 
        currentPost: post, 
        loading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch post',
        loading: false,
        currentPost: null
      });
    }
  },

  fetchTopicBySlug: async (topicSlug: string) => {
    set({ topicsLoading: true, error: null });
    
    try {
      const topic = await BlogApi.getTopicBySlug(topicSlug);
      set({ 
        currentTopic: topic, 
        topicsLoading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch topic details',
        topicsLoading: false,
        currentTopic: null
      });
    }
  },
  
  fetchPostsByTopic: async (topicSlug: string, filters?: Omit<BlogSearchFilters, 'topic_id'>) => {
    set({ loading: true, error: null });
    
    try {
      const result = await BlogApi.getPostsByTopic(topicSlug, filters);
      set({ 
        posts: result.posts,
        pagination: result.pagination,
        loading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch posts by topic',
        loading: false,
        posts: [],
        pagination: null
      });
    }
  },
  
  // Search actions
  searchPosts: async (query: string, filters?: Omit<BlogSearchQuery, 'q'>) => {
    set({ searchLoading: true, error: null });
    
    const searchQuery: BlogSearchQuery = {
      q: query,
      ...filters
    };
    
    try {
      const result = await BlogApi.searchPosts(searchQuery);
      set({ 
        searchResults: result.results,
        pagination: result.pagination,
        searchLoading: false,
        searchState: {
          ...get().searchState,
          searchQuery: query
        }
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to search posts',
        searchLoading: false,
        searchResults: [],
        pagination: null
      });
    }
  },
  
  clearSearchResults: () => {
    set({ 
      searchResults: [],
      searchState: {
        ...get().searchState,
        searchQuery: ''
      }
    });
  },
  
  // Pagination actions
  loadPage: async (page: number) => {
    const { searchState } = get();
    const filters: BlogSearchFilters = {
      search: searchState.searchQuery || undefined,
      topic_id: searchState.selectedTopicId,
      sort: searchState.sortBy,
      page,
      limit: searchState.limit
    };
    
    set({ searchState: { ...searchState, page } });
    await get().fetchPosts(filters);
  },
  
  loadNextPage: async () => {
    const { pagination } = get();
    if (pagination?.hasNextPage) {
      await get().loadPage(pagination.currentPage + 1);
    }
  },
  
  loadPrevPage: async () => {
    const { pagination } = get();
    if (pagination?.hasPrevPage) {
      await get().loadPage(pagination.currentPage - 1);
    }
  },
  
  // Filter actions
  updateSearchState: (state: Partial<BlogSearchState>) => {
    const currentState = get().searchState;
    set({ 
      searchState: { 
        ...currentState, 
        ...state 
      } 
    });
  },
  
  setSelectedTopic: (topic: PublicTopic | null) => {
    set({ 
      currentTopic: topic,
      searchState: {
        ...get().searchState,
        selectedTopicId: topic?.id
      }
    });
  },
  
  setSortOrder: async (sort: SortOption) => {
    const { searchState } = get();
    const newSearchState = { ...searchState, sortBy: sort, page: 1 };
    
    set({ searchState: newSearchState });
    
    const filters: BlogSearchFilters = {
      search: newSearchState.searchQuery || undefined,
      topic_id: newSearchState.selectedTopicId,
      sort: newSearchState.sortBy,
      page: newSearchState.page,
      limit: newSearchState.limit
    };
    
    await get().fetchPosts(filters);
  },
  
  // Interaction actions
  likePost: async (uuid: string) => {
    try {
      const result = await BlogApi.likePost(uuid);
      
      // Update post in current posts array
      set(state => ({
        posts: state.posts.map(post => 
          post.uuid === uuid 
            ? { ...post, totalLike: result.totalLikes }
            : post
        ),
        featuredPosts: state.featuredPosts.map(post => 
          post.uuid === uuid 
            ? { ...post, totalLike: result.totalLikes }
            : post
        ),
        currentPost: state.currentPost?.uuid === uuid 
          ? { ...state.currentPost, totalLike: result.totalLikes }
          : state.currentPost
      }));
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to like post'
      });
    }
  },
  
  sharePost: async (uuid: string, platform: 'facebook' | 'linkedin' | 'zalo') => {
    try {
      const result = await BlogApi.sharePost(uuid, platform);
      
      // Update post in current posts array
      set(state => ({
        posts: state.posts.map(post => 
          post.uuid === uuid 
            ? { ...post, totalShare: result.totalShares }
            : post
        ),
        featuredPosts: state.featuredPosts.map(post => 
          post.uuid === uuid 
            ? { ...post, totalShare: result.totalShares }
            : post
        ),
        currentPost: state.currentPost?.uuid === uuid 
          ? { ...state.currentPost, totalShare: result.totalShares }
          : state.currentPost
      }));
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to record share'
      });
    }
  },
  
  // Utility actions
  clearError: () => {
    set({ error: null });
  },
  
  resetState: () => {
    set({ 
      posts: [],
      featuredPosts: [],
      searchResults: [],
      currentPost: null,
      currentTopic: null,
      searchState: defaultSearchState,
      loading: false,
      searchLoading: false,
      error: null,
      pagination: null
    });
  },
}));